import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
    Account,
    AmountBounds,
    BusinessDetails,
    LineItem,
    User,
    UserLogin,
    VatConfig,
    WebAccountService,
    WebBusinessService,
    WebSystemConfigService,
    WebUserService
} from 'web-angular-client';
import {Router} from '@angular/router';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import {AlertService} from '../../service/alert.service';
import {AuthenticationService} from '../../service/authentication.service';
import {ConfirmationService} from '../../service/confirmation.service';
import {ROUTES} from '../../../app.routes';
import {SubmitForm} from '../../interface/form/submitForm';
import {combineLatest, Observable, of, Subject, take} from 'rxjs';
import {SyncValidators} from '../../validator/SyncValidators';
import {AsyncValidators} from '../../validator/AsyncValidators';
import {AccountNumberSansMerchantPrefixPipe} from '../../pipe/account-number-sans-merchant-prefix.pipe';
import {LayoutService} from '../../../secure/layout/layout.service';
import {SubscriptionService} from '../../service/subscription.service';
import {Helper} from '../../util/helper';
import {EnvironmentService} from '../../service/environment.service';
import {ConstantsUtil} from '../../util/constantsUtil';
import {DropdownItem} from '../../interface/data/DropdownItem';

@Component({
    selector: 'app-payment-request-form',
    templateUrl: './payment-request-form.component.html',
    styleUrls: ['./payment-request-form.component.scss'],
    standalone: false
})
/**
 * Defines the underlying variables and data structures needed to perform request to pay transaction
 * submissions and updates. This supports the request to pay transaction html submission form.
 */
export class PaymentRequestFormComponent extends SubmitForm implements OnInit
{
    private static A_DAY_IN_MILLI_SECONDS: number = 24 * 60 * 60 * 1000;
    @Input()
    mode: string;
    @Input()
    acceptButtonText: string = 'Save';
    @Input()
    cancelButtonText: string = 'Cancel';
    @Input()
    accountSubject: Subject<Account>;
    @Input()
    isQuickPay: boolean = false;
    @Output()
    accountSubmitEmitter = new EventEmitter<Account>();
    minimumRequestToPayAmount: number;
    maximumRequestToPayAmount: number;
    maximumExpiryPeriod: number;
    isCustomRefEnabled: Boolean;
    isMultiPremiumEnabled = false;
    sendPaymentNotification: Boolean = false;
    allowPartialPayments: Boolean = false;
    isUsingVatInclusiveAmount: Boolean = false;
    readonly multiPremiumOptions: DropdownItem[] = ConstantsUtil.MULTI_PREMIUM_OPTIONS;
    isVatRegistered: boolean;
    private vatRateUsed: number;

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected businessService: WebBusinessService,
        protected accountService: WebAccountService,
        protected environmentService: EnvironmentService,
        private layoutService: LayoutService,
        private subscriptionService: SubscriptionService,
        protected confirmationService: ConfirmationService,
        protected userService: WebUserService,
        protected systemConfigService: WebSystemConfigService,
        private accountNumberSansMerchantPrefix: AccountNumberSansMerchantPrefixPipe
    )
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
    }

    get getTooltipText(): string
    {
        if (this.isGuestAdminUser())
        {
            return 'Guest accounts cannot create request to pay transactions.';
        }
        else if (!this.isSubscriptionValid)
        {
            return 'Please pay your subscription fee before attempting to create a request to pay transaction.';
        }
        else if (!this.isVatRateLoaded)
        {
            return 'Request to pay creation is not available at the moment, please contact support.';
        }
        return '';
    }

    get isEditMode(): boolean
    {
        return this.mode === 'edit';
    }

    get isSubscriptionValid(): boolean
    {
        return this.subscriptionService.isSubscriptionValid;
    }

    get isVatRateLoaded(): boolean
    {
        return this.vatRateUsed != null;
    }

    get expiryDate(): string
    {
        return this.getDateTimeExpire();
    }

    ngOnInit()
    {
        this.loadSystemVat();
        this.form = this.formBuilder.group(this.isQuickPay ? this.getQuickPayControlsConfig() : this.getRequestToPayControlsConfig());

        const businessDetailsObs$ = this.businessService.getBusinessDetails();

        if (!this.isQuickPay)
        {
            this.accountService.getMaxRequestToPayExpiryPeriod().subscribe(value =>
            {
                this.maximumExpiryPeriod = value;
                if (!this.isEditMode)
                {
                    this.f.expiryPeriod.setValue(value);
                    this.setMaximumExpiryPeriodValidator();
                }
            });
        }

        // if our calling component (parent) provides an account, this account is used to pre-populate
        // the request to pay transaction form
        if (this.accountSubject)
        {
            combineLatest([
                businessDetailsObs$,
                this.accountSubject
            ]).pipe(take(1)).subscribe(([businessDetails, account]) =>
            {
                this.applyBusinessDetails(businessDetails);
                this.populateFormValuesFromAccount(account);
                this.addRequestToPayAmountValidators();
            });
        }
        else
        {
            businessDetailsObs$.subscribe((businessDetails: BusinessDetails) =>
            {
                this.applyBusinessDetails(businessDetails);
                // Regardless of whether or not an account was provided, we still need to know whether or not
                // the business is VAT registered, so we can switch certain form components on/off.
                this.isVatRegisteredObs().subscribe(value =>
                {
                    this.isVatRegistered = value;
                    this.addRequestToPayAmountValidators();
                });
            });
        }

        this.layoutService.loadPrepaidServicesStatusInfo();
    }

    addRequestToPayAmountValidators(): void
    {
        this.accountService.getMinMaxRequestToPayAmountBounds().subscribe({
            next: (amountBoundsDTO: AmountBounds) =>
            {
                this.minimumRequestToPayAmount = Helper.convertToMajorDenomination(amountBoundsDTO.minimumAmount);
                this.maximumRequestToPayAmount = Helper.convertToMajorDenomination(amountBoundsDTO.maximumAmount);

                const targetControl = this.isVatRegistered
                    ? this.f.amountInclVat
                    : this.f.totalAmountExclVat;

                targetControl.addValidators([
                    SyncValidators.minimumTotalValidator(this.minimumRequestToPayAmount),
                    SyncValidators.maximumTotalValidator(this.maximumRequestToPayAmount),
                ]);

                if (this.isMultiPremiumEnabled && !this.form.validator)
                {
                    this.form.addValidators(
                        SyncValidators.maximumMultiplesOfTotalValidator(
                            this.maximumRequestToPayAmount,
                            !!this.isVatRegistered
                        )
                    );
                    this.form.updateValueAndValidity({emitEvent: false});
                }
            },
            error: () =>
            {
                this.alertService.error('An error occurred while retrieving validators');
            }
        });
    }

    onSubmitAccount()
    {
        if (!this.isQuickPay)
        {
            const isValid = this.validateNumberOfLineItems();
            if (!isValid)
            {
                return;
            }
        }
        this.onSubmit();
    }

    submit(): void
    {
        this.accountSubmitEmitter.emit(this.isQuickPay ? this.getRTPFromQuickPayForm() : this.getRTPFromRTPForm());
    }

    cancel(): void
    {
        this.confirmationService.cancelConfirmation('request to pay transaction', 'Any changes made will be' +
            ' lost').then((confirmed) =>
        {
            if (confirmed)
            {
                this.router.navigate([ROUTES.SECURE.HOME.VIEW.path]).then();
            }
        });
    }

    calculateVatAmount()
    {
        this.f.amountInclVat.setValue((this.f.totalAmountExclVat.value * this.getVatRateForCalculation()).toFixed(
            2));
    }

    /**
     * A simplified version of the VAT rate used in calculations. Calculate as (% VAT / 100 + 1).
     */
    public getVatRateForCalculation(): number
    {
        return this.vatRateUsed / 100 + 1;
    }

    /**
     * Returns all the line items to be sent to the back-end. The individual amounts are changed to minor denomination.
     */
    getLineItemsToSubmit(): LineItem[]
    {
        let lineItems: LineItem[] = [];

        const lineItemsControl = <UntypedFormArray> this.f.lineItems;
        lineItemsControl.getRawValue().forEach(lineItem =>
        {
            const amountInCents = Helper.convertToMinorDenomination(lineItem.amount);
            lineItems.push({amount: amountInCents, description: lineItem.description});
        });

        return lineItems;
    }

    /**
     * Adds an empty line item to the lineItems {@link FormArray}.
     */
    addEmptyLineItem()
    {
        const lineItem = this.formBuilder.group({
            amount: new UntypedFormControl('0.00', Validators.required),
            description: new UntypedFormControl('', [Validators.required, Validators.maxLength(90)])
        });
        const lineItemsControl = <UntypedFormArray> this.f.lineItems;
        lineItemsControl.push(lineItem);
    }

    /**
     * Adds the line item to the lineItems {@link FormArray} and updates the total values (including and excluding VAT).
     */
    addLineItem(amount: number, description: string)
    {
        const lineItem = this.formBuilder.group({
            amount: new UntypedFormControl(amount.toFixed(2), Validators.required),
            description: new UntypedFormControl(description, [Validators.required, Validators.maxLength(90)])
        });
        const lineItemsControl = <UntypedFormArray> this.f.lineItems;
        lineItemsControl.push(lineItem);

        // update the total amount excluding VAT
        this.calculateTotalAmount();
    }

    /**
     * Removes the line item from the {@link FormArray} and updates the total values (including and excluding VAT).
     * @param index The index of the item to be removed.
     */
    removeLineItem(index: number)
    {
        const lineItemsControl = <UntypedFormArray> this.f.lineItems;
        lineItemsControl.removeAt(index);

        // update the total amount excluding VAT
        this.calculateTotalAmount();
    }

    /**
     * Sums all the line item amounts and updates the total amounts (including and excluding VAT).
     */
    calculateTotalAmount()
    {
        let totalAmount: string = '0';
        if (this.isQuickPay)
        {
            totalAmount = this.calculateQuickPayAmountExclVat();
        }
        else
        {
            totalAmount = this.calculateRequestToPayAmountExclVat();
        }

        if (this.isUsingVatInclusiveAmount)
        {
            this.f.totalAmountExclVat.setValue(this.getAmountExclVatToLoad(Helper.convertToMinorDenomination(+totalAmount))
                .toFixed(
                    2));
            this.f.amountInclVat.setValue((+totalAmount).toFixed(
                2));
        }
        else
        {
            this.f.totalAmountExclVat.setValue(totalAmount);
            // update the total amount including VAT
            this.calculateVatAmount();
        }

        this.f.totalAmountExclVat.markAsTouched();

        if (!!this.f.amountInclVat)
        {
            this.f.amountInclVat.markAsTouched();
        }
    }

    isAllowPartialPayments()
    {
        return this.allowPartialPayments ? ' YES' : ' NO';
    }

    isPaymentNotificationEnabled()
    {
        return this.sendPaymentNotification ? ' YES' : ' NO';
    }

    updateNotificationMobileNumber(value: any)
    {
        this.sendPaymentNotification = value;

        if (this.sendPaymentNotification)
        {
            this.f.notificationMobileNumber.enable();

            if (!this.f.notificationMobileNumber.value)
            {
                const user: UserLogin = this.authenticationService.getUserLogin();
                this.userService.getUserFromUserId(user.userId).subscribe((user: User) =>
                {
                    if (user.mobileNumber)
                    {
                        this.f.notificationMobileNumber.setValue(user.mobileNumber);
                    }
                });
            }
        }
        else
        {
            this.f.notificationMobileNumber.disable();
            this.f.notificationMobileNumber.setValue(null);
        }
    }

    private applyBusinessDetails(businessDetails: BusinessDetails)
    {
        this.isCustomRefEnabled = businessDetails.customRefEnabled;
        this.isVatRegistered = businessDetails.vatRegistered;
        this.isUsingVatInclusiveAmount = businessDetails.useVatInclusiveAmount;
        this.isMultiPremiumEnabled = businessDetails.multiPremiumEnabled;

        if ((!this.isCustomRefEnabled) || this.isEditMode)
        {
            this.disableCustomRefInput();
        }

        if (!this.isMultiPremiumEnabled)
        {
            this.f.multiPremiumOption.reset(1);
            this.f.multiPremiumOption.disable();
            this.form.updateValueAndValidity();
        }
    }

    /**
     * Determines whether the current business is VAT registered by checking if a VAT
     * number has been configured. This will determine whether VAT inclusive amount fields will be
     * displayed and used when submitting a request to pay transaction.
     *
     * @return true if a VAT number has been configured for the business, false otherwise.
     */
    private isVatRegisteredObs(): Observable<boolean>
    {
        return of(this.isVatRegistered);
    }

    private getRTPFromRTPForm(): Account
    {
        const requestToPay = this.getSharedRTPValuesFromForm();
        // the amount sent to the server needs to be received in minor denomination
        requestToPay.lineItems = this.getLineItemsToSubmit();
        requestToPay.description = this.f.description.value;
        requestToPay.customerEmail = this.f.customerEmail.value;
        return requestToPay;
    }

    private getRTPFromQuickPayForm()
    {
        const quickPayDescription = 'This request to pay transaction was created via Quick Pay';
        const requestToPay = this.getSharedRTPValuesFromForm();
        // the amount sent to the server needs to be received in minor denomination
        requestToPay.lineItems = [
            {
                amount: Helper.convertToMinorDenomination(this.f.quickPayAmount.getRawValue()),
                description: quickPayDescription
            }
        ];
        requestToPay.description = quickPayDescription;

        return requestToPay;
    }

    private getSharedRTPValuesFromForm()
    {
        const requestToPay = {} as Account;
        const totalAmount: number = this.getFinalAmountToSubmit();
        requestToPay.amount = totalAmount;
        requestToPay.maximumAmount = totalAmount;
        requestToPay.minimumAmount = this.allowPartialPayments ? Helper.convertToMinorDenomination(this.minimumRequestToPayAmount) : totalAmount;
        requestToPay.multiPremium = this.isMultiPremiumEnabled ? (+this.f.multiPremiumOption.value || null) : null;
        requestToPay.customer = this.f.customer.value;
        requestToPay.customerMobileNumber = this.f.customerMobileNumber.value;
        requestToPay.customerVatNumber = this.f.customerVatNumber.value;
        requestToPay.accountNumber = this.f.customReferenceNumber.value;
        requestToPay.referenceNumber = this.f.referenceNumber.value;
        requestToPay.dateTimeExpire = this.getDateTimeExpire();
        requestToPay.notificationMobileNumber = this.sendPaymentNotification ? this.f.notificationMobileNumber.value : null;
        return requestToPay;
    }

    /**
     * Populate the request to pay transaction form with values from an account by binding these values to the
     * reactive form component. A VAT registration check is done on the business, as this affects
     * how amount values are displayed in the request to pay transaction form.
     */
    private populateFormValuesFromAccount(account: Account)
    {
        this.isVatRegisteredObs().subscribe((isVatRegistered) =>
        {
            this.isVatRegistered = isVatRegistered;
            if (account)
            {
                // expect the amount from the server to arrive in minor denomination
                if (account.lineItems)
                {
                    account.lineItems.forEach(lineItem =>
                    {
                        const amountInMinor = Helper.convertToMajorDenomination(lineItem.amount);
                        this.addLineItem(amountInMinor, lineItem.description);
                    });
                }
                let amount = this.getAmountExclVatToLoad(account.amount).toFixed(2);
                this.f.totalAmountExclVat.setValue(amount);
                this.calculateVatAmount();
                this.f.multiPremiumOption.setValue(account.multiPremium);
                this.f.customer.setValue(account.customer);
                this.f.customerMobileNumber.setValue(account.customerMobileNumber);
                this.f.customerEmail.setValue(account.customerEmail);
                this.f.customerVatNumber.setValue(account.customerVatNumber);
                this.f.customReferenceNumber.setValue(this.accountNumberSansMerchantPrefix.transform(account.accountNumber));
                this.f.referenceNumber.setValue(account.referenceNumber);
                this.f.description.setValue(account.description);
                this.f.dateTimeExpire.setValue(account.dateTimeExpire);
                this.f.notificationMobileNumber.setValue(account.notificationMobileNumber);
                this.allowPartialPayments = account.minimumAmount != account.maximumAmount;
                this.setExpiryPeriod(account);
                this.updateNotificationMobileNumber(!!account.notificationMobileNumber);
            }
        });
    }

    private setExpiryPeriod(account: Account)
    {
        const daysElapsed: number = this.getDateDifferenceInDays(new Date().toString(), account.dateTimeCreated);
        const expiryPeriodLeft: number = this.getDateDifferenceInDays(
            account.dateTimeExpire,
            account.dateTimeCreated) - daysElapsed;

        this.maximumExpiryPeriod = this.maximumExpiryPeriod - daysElapsed;
        this.f.expiryPeriod.setValue(expiryPeriodLeft);
        this.setMaximumExpiryPeriodValidator();

        if (this.maximumExpiryPeriod < expiryPeriodLeft)
        {
            this.f.expiryPeriod.disable();
        }
    }

    private getDateDifferenceInDays(startDate: string, EndDate: string): number
    {
        const timeDifferenceInMilliSec = new Date(startDate).getTime() - new Date(EndDate).getTime();
        return Math.floor(timeDifferenceInMilliSec / PaymentRequestFormComponent.A_DAY_IN_MILLI_SECONDS);
    }

    private getDateTimeExpire(): string
    {
        const dateNowInMilliSeconds: number = new Date().getTime();
        const daysInMilliSeconds: number = this.f.expiryPeriod.value * PaymentRequestFormComponent.A_DAY_IN_MILLI_SECONDS;
        const dateInMilliSeconds: number = new Date(dateNowInMilliSeconds + daysInMilliSeconds).setHours(23, 59, 59, 0);
        return new Date(dateInMilliSeconds).toISOString();
    }

    /**
     * @return the amount incl. VAT if the business for which this payment is being made is VAT
     * registered, otherwise, for non-VAT businesses, the amount excl. VAT is returned. Amount is
     * returned in minor denomination.
     */
    private getFinalAmountToSubmit(): number
    {
        return Helper.convertToMinorDenomination(this.isVatRegistered ? (this.f.amountInclVat.value) : this.f.totalAmountExclVat.value);
    }

    private disableCustomRefInput(): void
    {
        this.f.customReferenceNumber.disable();
    }

    private getAmountExclVatToLoad(amountInMinorDenom: number): number
    {
        const amountInMajor: number = Helper.convertToMajorDenomination(amountInMinorDenom);

        return this.isVatRegistered ? (amountInMajor / this.getVatRateForCalculation()) : (amountInMajor);
    }

    private calculateQuickPayAmountExclVat(): string
    {
        const amountControl = <UntypedFormControl> this.f.quickPayAmount;
        const rawValue = amountControl.getRawValue();
        return Helper.cleanCurrencyValue(rawValue).toFixed(2);
    }

    private calculateRequestToPayAmountExclVat(): string
    {
        const lineItemsControl = <UntypedFormArray> this.f.lineItems;
        return lineItemsControl.getRawValue().reduce(
            (total, lineItem) => total + Helper.cleanCurrencyValue(lineItem.amount),
            0.00
        ).toFixed(2);
    }

    private setMaximumExpiryPeriodValidator()
    {
        this.f.expiryPeriod.addValidators([
            SyncValidators.maximumExpiryPeriod(this.maximumExpiryPeriod)
        ]);
    }

    private getRequestToPayControlsConfig(): any
    {
        return {
            ...this.getSharedControlsConfig(),
            lineItems: this.formBuilder.array([], [Validators.required]),
            customerEmail: ['', Validators.email],
            description: [''],
            dateTimeExpire: null,
            expiryPeriod: ['', [Validators.pattern('^\\d+$')]]
        };
    }

    private getQuickPayControlsConfig(): any
    {
        return {
            ...this.getSharedControlsConfig(),
            quickPayAmount: ['0.00', Validators.required],
            expiryPeriod: ['0'],
        };
    }

    private getSharedControlsConfig(): any
    {
        return {
            customer: ['', [Validators.required, Validators.maxLength(30)]],
            // 123.00 | 123
            totalAmountExclVat: [
                '0.00', [
                    Validators.required,
                    Validators.pattern('(?=.*?\\d)^\\$?(([1-9]\\d{0,2}(,\\d{3})*)|\\d+)?(\\.\\d{1,2})?$')
                ]
            ],
            amountInclVat: [
                '0.00', [
                    Validators.required,
                    Validators.pattern('(?=.*?\\d)^\\$?(([1-9]\\d{0,2}(,\\d{3})*)|\\d+)?(\\.\\d{1,2})?$')
                ]
            ],
            multiPremiumOption: [
                1,
                [Validators.required, Validators.min(1), Validators.max(12), Validators.pattern('^\\d+$')]
            ],
            customerMobileNumber: ['', [SyncValidators.validateMobileNumber()]],
            customerVatNumber: '',
            customReferenceNumber: [
                '', [
                    Validators.required,
                    Validators.pattern('^\\d+$')
                ]
                , [AsyncValidators.validateAccountNumber(this.accountService)]
            ],
            referenceNumber: ['', Validators.required],
            notificationMobileNumber: [
                {
                    value: null,
                    disabled: true
                }, [Validators.required, SyncValidators.validateMobileNumber()]
            ]
        };
    }

    private validateNumberOfLineItems(): boolean
    {
        const lineItemsControl = <UntypedFormArray> this.f.lineItems;
        if (lineItemsControl.getRawValue().length == 0)
        {
            this.alertService.error('You need to have at least 1 item to create a request to pay transaction.');
            return false;
        }
        else if (lineItemsControl.getRawValue().length > 20)
        {
            this.alertService.error('A request to pay transaction may not contain more than 20 items.');
            return false;
        }

        return true;
    }

    private loadSystemVat()
    {
        this.systemConfigService.getVatConfig().subscribe({
            next: (vatConfig: VatConfig) =>
            {
                this.vatRateUsed = vatConfig.value * 100;
            }, error: () =>
            {
                this.alertService.error(
                    'Request to pay creation is not available at the moment, please contact support.');
            }
        });
    }
}